// (c) chaehongee. Confidential and proprietary.

import { useCallback, useEffect, useRef, useState } from 'react'
import type { Point } from './types'

interface UseWorldTransformOptions {
    panDisabled?: boolean
}

interface UseWorldTransformResult {
    contentScale: number
    setContentScale: (v: number | ((p: number) => number)) => void
    contentOffset: Point
    setContentOffset: (v: Point | ((p: Point) => Point)) => void
    clientToWorld: (clientX: number, clientY: number) => Point | null
    onWheel: (e: React.WheelEvent<HTMLDivElement>) => void
    startPan: (e: React.PointerEvent<HTMLDivElement>) => boolean
    /** Force-start pan without middle-button/space check, and allows world-item targets. Used for grab tool mode. */
    startForcePan: (e: React.PointerEvent<HTMLDivElement>) => boolean
    movePan: (e: React.PointerEvent<HTMLDivElement>) => void
    endPan: (e: React.PointerEvent<HTMLDivElement>) => void
    isPanning: boolean
    spacePressed: boolean
}

export function useWorldTransform(
    containerRef: React.RefObject<HTMLDivElement | null>,
    options: UseWorldTransformOptions = {}
): UseWorldTransformResult {
    const { panDisabled } = options

    const [contentScale, setContentScale] = useState<number>(1)
    const [contentOffset, setContentOffset] = useState<Point>({ x: 0, y: 0 })
    const [isPanning, setIsPanning] = useState(false)
    const panStartRef = useRef<Point | null>(null)
    const [spacePressed, setSpacePressed] = useState(false)

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.code === 'Space') {
                setSpacePressed(true)
                const target = e.target as HTMLElement | null
                const isEditable =
                    !!target &&
                    (target.tagName === 'INPUT' ||
                        target.tagName === 'TEXTAREA' ||
                        (target as any).isContentEditable)
                if (!isEditable) {
                    e.preventDefault()
                }
            }
        }
        const onKeyUp = (e: KeyboardEvent) => {
            if (e.code === 'Space') setSpacePressed(false)
        }
        window.addEventListener('keydown', onKeyDown)
        window.addEventListener('keyup', onKeyUp)
        return () => {
            window.removeEventListener('keydown', onKeyDown)
            window.removeEventListener('keyup', onKeyUp)
        }
    }, [])

    const clientToWorld = useCallback((clientX: number, clientY: number) => {
        const el = containerRef.current
        if (!el) return null
        const r = el.getBoundingClientRect()
        const x = (clientX - r.left - contentOffset.x) / (contentScale || 1)
        const y = (clientY - r.top - contentOffset.y) / (contentScale || 1)
        return { x, y }
    }, [containerRef, contentOffset, contentScale])

    const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
        if (e.ctrlKey || e.metaKey) return
    }, [])

    useEffect(() => {
        const el = containerRef.current
        if (!el) return
        const onNativeWheel = (e: WheelEvent) => {
            if (!(e.ctrlKey || e.metaKey)) return
            e.preventDefault()
            const rect = el.getBoundingClientRect()
            const pointX = e.clientX - rect.left
            const pointY = e.clientY - rect.top
            const scaleBy = 1.15
            const direction = e.deltaY > 0 ? -1 : 1
            const nextScale = Math.max(
                0.1,
                Math.min(8, contentScale * (direction > 0 ? scaleBy : 1 / scaleBy))
            )
            const ratio = nextScale / (contentScale || 1)
            const nextOffset = {
                x: pointX - (pointX - contentOffset.x) * ratio,
                y: pointY - (pointY - contentOffset.y) * ratio,
            }
            setContentScale(nextScale)
            setContentOffset(nextOffset)
        }
        el.addEventListener('wheel', onNativeWheel, { passive: false })
        return () => {
            el.removeEventListener('wheel', onNativeWheel as EventListener)
        }
    }, [containerRef, contentScale, contentOffset])

    const startPan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (panDisabled) return false
        const t = e.target as Element
        if (t && t.closest('[data-canvas-ui="true"]')) return false
        if (t && t.closest('[data-world-item="true"]')) return false
        const middleButton = e.button === 1
        const leftWithSpace = e.button === 0 && spacePressed
        if (!middleButton && !leftWithSpace) {
            return false
        }
        const el = containerRef.current
        if (!el) return false
        el.setPointerCapture(e.pointerId)
        setIsPanning(true)
        panStartRef.current = { x: e.clientX - contentOffset.x, y: e.clientY - contentOffset.y }
        return true
    }, [containerRef, panDisabled, spacePressed, contentOffset])

    const startForcePan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (panDisabled) return false
        const t = e.target as Element
        if (t && t.closest('[data-canvas-ui="true"]')) return false
        // Intentionally does NOT check data-world-item — used for grab mode
        const el = containerRef.current
        if (!el) return false
        el.setPointerCapture(e.pointerId)
        setIsPanning(true)
        panStartRef.current = { x: e.clientX - contentOffset.x, y: e.clientY - contentOffset.y }
        return true
    }, [containerRef, panDisabled, contentOffset])

    const rafIdRef = useRef<number | null>(null)

    const movePan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        if (!isPanning) return
        const start = panStartRef.current
        if (!start) return
        const nextOffset = { x: e.clientX - start.x, y: e.clientY - start.y }
        if (rafIdRef.current !== null) return
        rafIdRef.current = requestAnimationFrame(() => {
            rafIdRef.current = null
            setContentOffset(nextOffset)
        })
    }, [isPanning])

    const endPan = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
        const el = containerRef.current
        if (!isPanning) return
        if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
            rafIdRef.current = null
        }
        if (el) {
            try { el.releasePointerCapture(e.pointerId) } catch { }
        }
        setIsPanning(false)
        panStartRef.current = null
    }, [containerRef, isPanning])

    return {
        contentScale,
        setContentScale,
        contentOffset,
        setContentOffset,
        clientToWorld,
        onWheel,
        startPan,
        startForcePan,
        movePan,
        endPan,
        isPanning,
        spacePressed,
    }
}

